import { useLanguage, useTranslate } from '@jablotron/lib.spa-lang';
import { Link } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { Overline } from '~/components/overline/Overline';
import { subcategoryProductCount, visibleSubFilters } from '~/entities/catalog/api';
import { categoryPath, subcategoryPath } from '~/entities/catalog/paths';
import {
  SIDEBAR_ICON_BY_SUBCATEGORY_ID,
  SIDEBAR_SECTION_BY_SUBCATEGORY_ID,
  SIDEBAR_SECTIONS,
  type SidebarSectionKey,
} from '~/entities/catalog/sidebar-sections';
import type { Catalog } from '~/entities/catalog/types';
import { useLocalisedHref } from '~/entities/urls/use-localised-href';
import { Texts } from '~/texts/runtime';

/** Product lines whose subcategories are grouped into sections (see spec Scope). */
const GROUPED_CATEGORY_IDS = new Set(['1', '2']);

export type SidebarSubFilterItemChild = {
  label: string;
  href: string;
};

export type SidebarSubFilterItem = {
  label: string;
  href: string;
  children?: SidebarSubFilterItemChild[];
};

export type SidebarSubFilter = {
  label: string;
  href: string;
  subItems?: SidebarSubFilterItem[];
};

export type SidebarSubcategory = {
  id: string;
  slug: string;
  name: string;
  href: string;
  image?: string;
  subFilters?: SidebarSubFilter[];
  section?: SidebarSectionKey;
  icon?: string;
  count?: number;
};

export type SidebarCategory = {
  id: string;
  slug: string;
  title: string;
  href: string;
  subcategories: SidebarSubcategory[];
};

type CatalogSidebarProps = {
  categories: SidebarCategory[];
  activeCategoryId?: string;
  activeSubcategoryId?: string;
  activeSubFilterLabel?: string;
  activeSubFilterItemLabel?: string;
  activeChildLabel?: string;
  allHref?: string;
  /** Whether the "Všechny produkty" link should render as active. Defaults to `!activeCategoryId`, which is correct for
   *  category-scoped pages and the all-products listing itself, but pages outside that tree (e.g. Compatibility) pass
   *  `false` so neither a category nor "Všechny produkty" appears selected. */
  allActive?: boolean;
  className?: string;
  /** Called when a nav link is activated (e.g. close mobile drawer). */
  onNavigate?: () => void;
};

export type { CatalogSidebarProps };

export function buildSidebarCategories(
  catalog: Catalog,
  options?: { activeSubcategoryId?: string },
): SidebarCategory[] {
  return catalog.categories.map((cat) => {
    const page = catalog.categoryPages.find((p) => p.id === cat.id);
    const subs = page?.subcategories ?? [];
    const images = page?.subcategoryImages;

    const categorySlug = cat.slug || cat.id;
    const isGrouped = GROUPED_CATEGORY_IDS.has(cat.id);

    return {
      id: cat.id,
      slug: categorySlug,
      title: cat.title,
      href: categoryPath(categorySlug),
      subcategories: subs.map((sub, i) => {
        const id = typeof sub === 'string' ? `${cat.id}_${i}` : sub.id ?? `${cat.id}_${i}`;
        const subPage = catalog.subcategoryPages.find((p) => p.id === id);
        const subSlug = subPage?.slug || id;
        const name = subPage?.title ?? (typeof sub === 'string' ? sub : sub.title);
        const image = Array.isArray(images) ? images[i] : images?.[String(i)];
        const activeSubPage =
          options?.activeSubcategoryId === id || options?.activeSubcategoryId === subSlug ? subPage : undefined;
        const base = subcategoryPath(categorySlug, subSlug);
        // Same rule as the tile row on the page itself, so the two can't disagree about which
        // sub-filters exist.
        const shownSubFilters = activeSubPage
          ? visibleSubFilters(activeSubPage.subFilters, catalog.products, activeSubPage.id)
          : undefined;

        return {
          id,
          slug: subSlug,
          name,
          href: base,
          image,
          section: isGrouped ? SIDEBAR_SECTION_BY_SUBCATEGORY_ID[id] : undefined,
          icon: isGrouped ? SIDEBAR_ICON_BY_SUBCATEGORY_ID[id] : undefined,
          count: isGrouped ? subcategoryProductCount(catalog.products, id) : undefined,
          subFilters: shownSubFilters?.map((sf) => ({
            label: sf.label,
            href: `${base}?sub=${encodeURIComponent(sf.label)}`,
            subItems: sf.subItems?.map((si) => ({
              label: si.label,
              href: `${base}?sub=${encodeURIComponent(sf.label)}&item=${encodeURIComponent(si.label)}`,
              children: si.children?.map((ch) => ({
                label: ch.label,
                href: `${base}?sub=${encodeURIComponent(sf.label)}&item=${encodeURIComponent(
                  si.label,
                )}&child=${encodeURIComponent(ch.label)}`,
              })),
            })),
          })),
        };
      }),
    };
  });
}

export function CatalogSidebar({
  categories,
  activeCategoryId,
  activeSubcategoryId,
  activeSubFilterLabel,
  allHref,
  allActive = !activeCategoryId,
  className = '',
  onNavigate,
}: CatalogSidebarProps) {
  const t = useTranslate();
  const [currentLanguage] = useLanguage();
  const localise = useLocalisedHref();
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(() => {
    const activeCat = categories.find((cat) => cat.id === activeCategoryId || cat.slug === activeCategoryId);

    return activeCat ? activeCat.id : null;
  });

  const [subExpanded, setSubExpanded] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    const activeCat = categories.find((c) => c.id === activeCategoryId || c.slug === activeCategoryId);
    if (activeSubcategoryId) {
      init[activeSubcategoryId] = true;
    } else if (activeCat) {
      activeCat.subcategories.forEach((sub) => {
        init[sub.id] = true;
      });
    }

    return init;
  });

  const toggleCategory = (id: string) => setExpandedCategoryId((prev) => (prev === id ? null : id));
  const toggleSub = useCallback((id: string) => setSubExpanded((prev) => ({ ...prev, [id]: !prev[id] })), []);
  const openSub = useCallback((id: string) => setSubExpanded((prev) => ({ ...prev, [id]: true })), []);

  const renderSubcategoryItem = (cat: SidebarCategory, sub: SidebarSubcategory) => {
    const isSubActive = sub.id === activeSubcategoryId || sub.slug === activeSubcategoryId;
    const hasSubFilters = Boolean(sub.subFilters?.length);
    const isSubExpanded = !!subExpanded[sub.id] || !!subExpanded[sub.slug];

    return (
      <li key={sub.id} data-testid="sidebar-item" data-subcategory-id={sub.id}>
        <Link
          to="/categories/$categoryId/$subcategoryId"
          params={{
            categoryId: cat.slug,
            subcategoryId: sub.slug,
          }}
          onClick={() => {
            onNavigate?.();
            // subFilters are only populated for the currently-active subcategory (see buildSidebarCategories),
            // so `hasSubFilters` is stale for the item being navigated to — don't gate on it here.
            if (isSubActive) {
              toggleSub(sub.id);
            } else {
              openSub(sub.id);
            }
          }}
          className={`flex items-center gap-2 rounded py-1.5 pl-2 pr-5 text-small leading-snug no-underline ${
            isSubActive ? 'font-semibold text-brand-ink' : 'font-normal text-brand-muted hover:bg-brand-surface'
          }`}
        >
          {sub.icon && (
            <img src={sub.icon} alt="" aria-hidden data-testid="sidebar-item-icon" className="size-5 shrink-0" />
          )}
          <span className="min-w-0 flex-1 hyphens-auto break-words pr-7">{t(sub.name)}</span>
          {typeof sub.count === 'number' && (
            <span data-testid="sidebar-item-count" className="shrink-0 text-caption font-normal text-brand-subtle">
              {sub.count}
            </span>
          )}
        </Link>

        {isSubExpanded && hasSubFilters && (
          <ul className="mb-1 ml-[18px] mt-px flex list-none flex-col gap-px p-0">
            {sub.subFilters!.map((sf) => {
              const sfActive = sf.label === activeSubFilterLabel;

              return (
                <li key={sf.label}>
                  <a
                    href={localise(sf.href)}
                    onClick={onNavigate}
                    className={`block rounded py-1.5 pl-3.5 pr-1 text-small leading-snug no-underline ${
                      sfActive
                        ? 'bg-brand-cyanSoft font-semibold text-brand-cyanText'
                        : 'font-normal text-brand-subtle hover:bg-brand-surface'
                    }`}
                  >
                    {t(sf.label)}
                  </a>
                </li>
              );
            })}
          </ul>
        )}
      </li>
    );
  };

  return (
    // `lang` drives the browser's hyphenation dictionary; <html lang> stays "cs", so German labels would break at the wrong places.
    <aside
      lang={currentLanguage}
      className={className ? className : 'sticky top-16 h-full w-[228px] shrink-0 pb-12 pr-4 pt-6'}
    >
      <div className="relative">
        <div className="mb-2 pl-2 pr-1">
          <Overline as="h3">{t(Texts.MegaMenuLinesHeading)}</Overline>
        </div>

        <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
          {categories.map((cat) => {
            const isCatActive = cat.id === activeCategoryId || cat.slug === activeCategoryId;
            const isExpanded = cat.id === expandedCategoryId;

            return (
              <li key={cat.id}>
                <Link
                  to="/categories/$categoryId"
                  params={{ categoryId: cat.slug }}
                  onClick={() => {
                    onNavigate?.();
                    toggleCategory(cat.id);
                  }}
                  className={`-ml-2 block rounded-lg border-l-[3px] py-2 pl-4 pr-7 text-small font-semibold leading-snug no-underline ${
                    isCatActive
                      ? 'border-l-brand-cyan bg-brand-cyanSoft text-brand-cyanText'
                      : 'border-l-transparent text-brand-ink hover:bg-brand-surface hover:text-brand-cyanText'
                  }`}
                >
                  {t(cat.title)}
                </Link>

                {isExpanded &&
                  cat.subcategories.length > 0 &&
                  (cat.subcategories.some((sub) => sub.section) ? (
                    <div className="-mr-4 mb-1.5">
                      {SIDEBAR_SECTIONS.map((section) => {
                        const items = cat.subcategories.filter((sub) => sub.section === section.key);
                        if (items.length === 0) {
                          return null;
                        }

                        return (
                          <div key={section.key}>
                            <div
                              data-testid="sidebar-section-heading"
                              data-section={section.key}
                              className="px-2 pb-1.5 pt-3 text-caption font-normal uppercase tracking-wide text-brand-subtle"
                            >
                              {t(Texts[section.textsKey])}
                            </div>
                            <ul className="flex list-none flex-col gap-px p-0">
                              {items.map((sub) => renderSubcategoryItem(cat, sub))}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <ul className="-mr-4 mb-1.5 flex list-none flex-col gap-px p-0">
                      {cat.subcategories.map((sub) => renderSubcategoryItem(cat, sub))}
                    </ul>
                  ))}
              </li>
            );
          })}
        </ul>

        {allHref && (
          <div className="mt-2.5 border-t border-primary pt-1.5">
            <Link
              to="/products"
              onClick={onNavigate}
              className={`-ml-2 block rounded-lg border-l-[3px] py-2 pl-4 pr-7 text-small font-semibold leading-snug no-underline ${
                allActive
                  ? 'border-l-brand-cyan bg-brand-cyanSoft text-brand-cyanText'
                  : 'border-l-transparent text-brand-ink hover:bg-brand-surface hover:text-brand-cyanText'
              }`}
            >
              {t(Texts.CatalogAllProducts)}
            </Link>
          </div>
        )}
      </div>
    </aside>
  );
}
